import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { AuthThumb, AuthPhoto } from "../components/AuthPhoto";
import { useSwipe } from "../hooks/useSwipe";

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

export default function Gallery({ role, canDelete }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [checkedPhotos, setCheckedPhotos] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => { fetchPhotos(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyDown = useCallback((e) => {
    if (selected === null) return;
    if (e.key === "ArrowRight") goNext();
    if (e.key === "ArrowLeft") goPrev();
    if (e.key === "Escape") closeLightbox();
  }, [selected, selectedIndex, photos]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  async function fetchPhotos() {
    const { data, error } = await supabase
      .from("photos")
      .select("*, albums(name)")
      .order("uploaded_at", { ascending: false });
    if (!error) setPhotos(data);
    setLoading(false);
  }

  function openPhoto(photo, index) {
    if (selectMode) { toggleCheck(photo); return; }
    setSelected(photo);
    setSelectedIndex(index);
  }

  function closeLightbox() { setSelected(null); setSelectedIndex(null); }

  function goNext() {
    const nextIndex = (selectedIndex + 1) % photos.length;
    setSelected(photos[nextIndex]);
    setSelectedIndex(nextIndex);
  }

  function goPrev() {
    const prevIndex = (selectedIndex - 1 + photos.length) % photos.length;
    setSelected(photos[prevIndex]);
    setSelectedIndex(prevIndex);
  }

  const swipeHandlers = useSwipe({ onSwipeLeft: goNext, onSwipeRight: goPrev });

  function toggleCheck(photo) {
    setCheckedPhotos((prev) =>
      prev.find((p) => p.id === photo.id)
        ? prev.filter((p) => p.id !== photo.id)
        : [...prev, photo]
    );
  }

  function selectAll() { setCheckedPhotos([...photos]); }
  function clearSelection() { setCheckedPhotos([]); setSelectMode(false); }

  async function deleteSinglePhoto(photo) {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    setDeleting(true);
    const headers = await getAuthHeaders();
    const { error } = await supabase.from("photos").delete().eq("id", photo.id);
    if (!error) {
      await fetch(`/api/delete`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename: photo.file_path }),
      });
      const updated = photos.filter((p) => p.id !== photo.id);
      setPhotos(updated);
      if (updated.length === 0) closeLightbox();
      else {
        const nextIndex = Math.min(selectedIndex, updated.length - 1);
        setSelected(updated[nextIndex]);
        setSelectedIndex(nextIndex);
      }
    }
    setDeleting(false);
  }

  async function bulkDelete() {
    if (checkedPhotos.length === 0) return;
    if (!window.confirm(`Delete ${checkedPhotos.length} photo(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const headers = await getAuthHeaders();
    for (const photo of checkedPhotos) {
      await supabase.from("photos").delete().eq("id", photo.id);
      await fetch(`/api/delete`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename: photo.file_path }),
      });
    }
    const deletedIds = checkedPhotos.map((p) => p.id);
    setPhotos((prev) => prev.filter((p) => !deletedIds.includes(p.id)));
    setCheckedPhotos([]);
    setSelectMode(false);
    setBulkDeleting(false);
  }

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="album-page-header">
          <div>
            <h1 className="page-title">All Photos</h1>
            <p className="page-subtitle">{photos.length} photos</p>
          </div>
          {canDelete && photos.length > 0 && (
            <div className="admin-controls">
              {!selectMode ? (
                <button className="select-btn" onClick={() => setSelectMode(true)}>☑️ Select Photos</button>
              ) : (
                <div className="select-actions">
                  <span className="select-count">{checkedPhotos.length} selected</span>
                  <button className="select-btn" onClick={selectAll}>Select All</button>
                  <button className="select-btn" onClick={clearSelection}>Cancel</button>
                  <button className="delete-bulk-btn" onClick={bulkDelete} disabled={checkedPhotos.length === 0 || bulkDeleting}>
                    {bulkDeleting ? "Deleting..." : `🗑️ Delete (${checkedPhotos.length})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📷</div>
          <p>No photos yet — check back soon!</p>
        </div>
      ) : (
        <div className="photo-grid">
          {photos.map((photo, index) => {
            const isChecked = checkedPhotos.find((p) => p.id === photo.id);
            return (
              <div
                key={photo.id}
                className={`photo-card ${selectMode ? "selectable" : ""} ${isChecked ? "photo-checked" : ""}`}
              >
                {/* Use thumbnail for grid */}
                <AuthThumb
                  filename={photo.file_path}
                  alt={photo.caption || "Family photo"}
                  onClick={() => openPhoto(photo, index)}
                />
                {selectMode && (
                  <div
                    className={`check-overlay ${isChecked ? "checked" : ""}`}
                    onClick={() => toggleCheck(photo)}
                  >
                    {isChecked ? "✓" : ""}
                  </div>
                )}
                {photo.caption && <div className="photo-caption">{photo.caption}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox - uses full resolution */}
      {selected && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()} {...swipeHandlers}>
            <button className="lightbox-close" onClick={closeLightbox}>✕</button>
            <div className="lightbox-counter">{selectedIndex + 1} / {photos.length}</div>
            <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} disabled={photos.length <= 1}>‹</button>
            <AuthPhoto filename={selected.file_path} alt={selected.caption || "Family photo"} className="lightbox-img" />
            <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); goNext(); }} disabled={photos.length <= 1}>›</button>
            <div className="lightbox-footer">
              {selected.caption && <p className="lightbox-caption">{selected.caption}</p>}
              {selected.albums && <p className="lightbox-album">📁 {selected.albums.name}</p>}
              {canDelete && (
                <button className="delete-btn" onClick={() => deleteSinglePhoto(selected)} disabled={deleting}>
                  {deleting ? "Deleting..." : "🗑️ Delete Photo"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}